/**
 * SandboxToggle - Header toggle for sandbox mode (ADR-006).
 *
 * When active, the system uses reference_time instead of now(),
 * enabling maneuver planning against historical conjunctions.
 */

import { useState } from 'react'
import { FlaskConical } from 'lucide-react'

interface SandboxToggleProps {
  enabled: boolean
  referenceTime: string | null
  onToggle: (enabled: boolean, referenceTime: string | null) => void
}

function SandboxToggle({ enabled, referenceTime, onToggle }: SandboxToggleProps) {
  const [showPicker, setShowPicker] = useState(false)

  const handleToggle = () => {
    if (enabled) {
      // Turning off — clear reference time
      onToggle(false, null)
      setShowPicker(false)
    } else {
      // Turning on — show datetime picker
      setShowPicker(true)
    }
  }

  const handleDateTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value) {
      // Convert local datetime to ISO string with Z suffix
      const dt = new Date(value)
      onToggle(true, dt.toISOString())
    }
  }

  const handlePickerClose = () => {
    if (!enabled) {
      setShowPicker(false)
    }
  }

  // Format reference time for display
  const displayTime = referenceTime
    ? new Date(referenceTime).toISOString().replace('T', ' ').slice(0, 19) + 'Z'
    : null

  return (
    <div className="relative flex items-center gap-2">
      <button
        onClick={handleToggle}
        className={`
          flex items-center gap-1.5 px-2 py-1 rounded border text-xs font-mono
          transition-all duration-200
          ${
            enabled
              ? 'border-retro-amber text-retro-amber bg-retro-amber/10'
              : 'border-retro-border text-retro-dim hover:text-retro-text hover:border-retro-text'
          }
        `}
        title={
          enabled
            ? `SANDBOX MODE: reference_time=${displayTime}`
            : 'Enable sandbox mode for maneuver planning against historical data'
        }
      >
        <FlaskConical className="w-3 h-3" />
        <span className="tracking-wider uppercase">
          {enabled ? 'SANDBOX' : 'SANDBOX'}
        </span>
        {enabled && (
          <div
            className="w-1.5 h-1.5 rounded-full animate-pulse"
            style={{ backgroundColor: '#ffcc66' }}
          />
        )}
      </button>

      {/* Datetime picker popover */}
      {showPicker && !enabled && (
        <div
          className="absolute top-full right-0 mt-1 p-3 rounded border border-retro-border bg-retro-bg shadow-lg z-50"
          onBlur={handlePickerClose}
        >
          <label className="block text-xs text-retro-dim mb-1 tracking-wider uppercase">
            Reference Time (UTC)
          </label>
          <input
            type="datetime-local"
            className="
              bg-retro-surface border border-retro-border rounded px-2 py-1
              text-xs font-mono text-retro-text
              focus:outline-none focus:border-retro-amber
            "
            onChange={handleDateTimeChange}
            autoFocus
          />
          <p className="text-[10px] text-retro-subtle mt-1 max-w-48">
            System will treat this as &quot;now&quot; for TCA filtering and maneuver planning.
          </p>
        </div>
      )}

      {/* Active sandbox indicator with time */}
      {enabled && displayTime && (
        <span className="text-[10px] text-retro-amber font-mono hidden sm:inline">
          T={displayTime}
        </span>
      )}
    </div>
  )
}

export default SandboxToggle
